import type { FunnelStage, FunnelView } from './PredictionFunnel';
import FunnelStageProvenance from './FunnelStageProvenance';
import FunnelStageLiveness from './FunnelStageLiveness';

interface FunnelStageRowProps {
  stage: FunnelStage;
  total: number;
  selected?: boolean;
  onClickSurvivors: () => void;
  onClickDrop: () => void;
}

const categoryLabels: Record<string, string> = {
  by_design: 'By design',
  fixable: 'Fixable',
  waiting: 'Waiting on time',
};

const categoryClasses: Record<string, string> = {
  by_design:
    'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  fixable:
    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  waiting:
    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

export default function FunnelStageRow({
  stage,
  total,
  selected,
  onClickSurvivors,
  onClickDrop,
}: FunnelStageRowProps) {
  const widthPct = Math.max(2, (stage.count / total) * 100);
  const hasDrop = stage.drop > 0;

  return (
    <div
      className={`flex items-stretch gap-2 rounded-lg border transition-colors ${
        selected
          ? 'border-blue-400 bg-blue-50/50 dark:border-blue-600 dark:bg-blue-900/10'
          : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50'
      }`}
    >
      <button
        type="button"
        onClick={onClickSurvivors}
        className="flex-1 flex items-center gap-3 px-3 py-2 text-left"
        aria-label={`${stage.label}: ${stage.count.toLocaleString()} survivors`}
      >
        <div className="w-40 shrink-0">
          <div className="text-xs text-gray-500 dark:text-gray-400">{stage.label}</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {stage.count.toLocaleString()}
          </div>
          <div className="flex flex-col items-start">
            <FunnelStageProvenance stageKey={stage.key} provenance={stage.provenance} />
            <FunnelStageLiveness liveness={stage.liveness} />
          </div>
        </div>
        <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden">
          <div
            className="h-full bg-blue-500 dark:bg-blue-600 rounded min-w-[3px]"
            style={{ width: `${widthPct}%` }}
          />
        </div>
      </button>

      {hasDrop ? (
        <button
          type="button"
          onClick={onClickDrop}
          className="shrink-0 flex flex-col justify-center items-end px-3 py-2 text-right min-w-[120px]"
          aria-label={`${stage.drop.toLocaleString()} dropped: ${stage.drop_label ?? ''}`}
        >
          <span className="text-sm font-semibold text-red-600 dark:text-red-400">
            −{stage.drop.toLocaleString()}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {stage.drop_label}
          </span>
          {stage.category && (
            <span
              className={`mt-0.5 inline-block text-[10px] px-1.5 py-0.5 rounded font-medium ${
                categoryClasses[stage.category]
              }`}
            >
              {categoryLabels[stage.category]}
            </span>
          )}
        </button>
      ) : (
        <div className="shrink-0 flex items-center px-3 py-2 text-right text-xs text-gray-400 dark:text-gray-500 min-w-[120px]">
          {stage.category === null ? 'No loss' : '—'}
        </div>
      )}
    </div>
  );
}

export type { FunnelStage, FunnelView };
